/**
 * @fileoverview ADLSPage Component - Activities of Daily Living Statistics Page
 * 
  * This component displays a comprehensive dashboard for tracking and visualizing
  * Activities of Daily Living (ADLs) for senior care residents. It shows real-time
  * data for three primary activities: sitting, walking, and standing.
 * 
 * Features:
 * - Real-time activity duration tracking
 * - Responsive bar charts showing activity patterns throughout the day
 * - Mobile-responsive design with adaptive layouts
 * - Loading and error states with retry functionality
 * - Optional navigation back to resident details
 * 
 * Data Structure Expected:
 * The component expects ADLS data in the following format:
 * {
 *   residentName: string,
  *   sit: { totalDuration: { hours, minutes }, chartData: [{time, duration}] },
  *   walk: { totalDuration: { hours, minutes }, chartData: [{time, duration}] },
  *   stand: { totalDuration: { hours, minutes }, chartData: [{time, duration}] }
  * }
 * 
 * @exports ADLSPage - Main page component
 */

import { useState, useEffect } from "react";
import sitIcon from "../assets/Sit.svg";
import walkIcon from "../assets/gait.svg";
import standIcon from "../assets/Stand.svg";
import useWindowSize from "../hooks/useWindowSize";
import apiService from "../services/api";
import cacheService from "../services/cacheService";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import "../styles/pages/ADLSPage.css";

/* ═══════════════════════ CONSTANTS ═══════════════════════ */

/**
 * White color constant used throughout the component for text and borders.
 * @constant {string}
 */
const W = "#ffffff";

/* ═══════════════════════ HELPER COMPONENTS ═══════════════════════ */

/**
 * GCard - Gradient Card Component
 * 
 * A reusable card component with a gradient blue background, white border,
 * and a subtle bottom gradient overlay for visual depth.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to render inside the card
 * @param {string} [props.variant="default"] - Card variant: "default", "info", or "chart"
 * @param {boolean} [props.isMobile=false] - Whether to apply mobile styles
 * @returns {JSX.Element} Styled card container with gradient background
 */
const GCard = ({ children, variant = "default", isMobile = false }) => {
  const cardClass = `adls-card ${isMobile ? 'mobile' : ''} ${
    variant === 'info' ? 'adls-card--info' : 
    variant === 'chart' ? 'adls-card--chart' : 
    ''
  }`;
  
  return (
    <div className={cardClass}>
      <div className="adls-card__gradient-overlay" />
      <div className="adls-card__content">
        {children}
      </div>
    </div>
  );
};

/**
 * CustomBar - Custom Bar Shape for Recharts
 * 
 * Renders a custom bar shape for the bar chart with rounded corners.
 * This component is used by Recharts to render individual bars in the chart.
 * 
 * @param {Object} props - Bar shape properties provided by Recharts
 * @param {number} props.x - X-coordinate of the bar
 * @param {number} props.y - Y-coordinate of the bar
 * @param {number} props.width - Width of the bar
 * @param {number} props.height - Height of the bar
 * @param {string} props.fill - Fill color (overridden to white)
 * @returns {JSX.Element} SVG rect element with rounded corners
 */
const CustomBar = ({ x, y, width, height }) => {
  return <rect x={x} y={y} width={width} height={height} fill="#ffffff" rx={2} />;
};

/* ═══════════════════════ MAIN PAGE COMPONENT ═══════════════════════ */

/**
 * ADLSPage - Activities of Daily Living Statistics Page
 * 
 * Main page component that displays activity tracking data for a resident.
 * Shows three activity types (sit, walk, stand) with duration summaries and
 * time-series bar charts. Handles data loading, error states, and responsive layout.
 * 
 * @param {Object} props - Component props
 * @param {string} [props.residentId] - ID of the resident to display data for.
 *                                       Falls back to RESIDENT_ID constant if not provided.
 * @param {boolean} [props.showBackButton] - Whether to show the back navigation button
 * @param {Function} [props.onBackToResident] - Callback function when back button is clicked
 * @returns {JSX.Element} The complete ADLS page with activity cards and charts
 */
export default function ADLSPage({ residentId, showBackButton, onBackToResident }) {
    /* ──────────────────────── State Management ──────────────────────── */
  
  /** @type {[Object|null, Function]} ADLS data containing activity information */
  const [adlsData, setAdlsData] = useState(null);
  
  /** @type {[boolean, Function]} Loading state for data fetching */
  const [loading, setLoading] = useState(true);
  
  /** @type {[Error|null, Function]} Error state for failed data fetching */
  const [error, setError] = useState(null);
  
  /** @type {Object} Window size hook providing isMobile boolean */
  const { isMobile } = useWindowSize();

    /**
   * Active resident ID - uses provided residentId prop (no default fallback).
   */
  const activeResidentId = residentId;

    /* ──────────────────────── Data Fetching ──────────────────────── */
  
  /**
   * Loads ADLS data for the active resident from the API.
   * 
   * This async function:
   * 1. Sets loading state to true and clears any previous errors
   * 2. Fetches ADLS data from the API service
   * 3. Updates the adlsData state with the fetched data
   * 4. Handles errors by logging them and setting error state
   * 5. Always sets loading to false when complete
   * 
   * @async
   * @function loadADLSData
   * @returns {Promise<void>}
   * @throws {Error} If the API call fails, error is caught and stored in state
   */
  const loadADLSData = async () => {
    if (!activeResidentId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Clear cache for this resident so retry always hits the network
      cacheService.clearPattern(activeResidentId);
      cacheService.delete(`api:/adls/${encodeURIComponent(activeResidentId)}/`);
      
      const data = await apiService.getADLSData(activeResidentId);
      setAdlsData(data);
    } catch (err) {
      console.error('Failed to load ADLS data:', err);
      console.error('Resident ID that failed:', activeResidentId);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

    /* ──────────────────────── Effects ──────────────────────── */
  
  /**
   * Effect: Load ADLS data when component mounts or activeResidentId changes.
   * 
   * Note: eslint-disable is used because loadADLSData is not memoized.
   * In production, consider wrapping loadADLSData in useCallback to fix this properly.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadADLSData(); }, [activeResidentId]);

    /* ──────────────────────── Render: Loading State ──────────────────────── */
  
  /**
   * Loading State Render
   * Displays a loading spinner while data is being fetched.
   * Also shows the back button if enabled.
   */
    if (loading) {
    return (
      <main className="adls-page--centered">
        {showBackButton && onBackToResident && (
          <button
            onClick={onBackToResident}
            className={`adls-back-button ${isMobile ? 'mobile' : ''}`}
          >
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            {isMobile ? "Back" : "Back to Resident Details"}
          </button>
        )}
        <LoadingSpinner message="Loading ADLs data..." />
      </main>
    );
  }

    /* ──────────────────────── Render: Error State ──────────────────────── */
  
  /**
   * Error State Render
   * Displays an error message when data fetching fails or no data is available.
   * Provides a retry button and shows the back button if enabled.
   */
          if (!loading && (error || !adlsData)) {
    return (
      <main className="adls-page--centered">
        {showBackButton && onBackToResident && (
          <button
            onClick={onBackToResident}
            className={`adls-back-button ${isMobile ? 'mobile' : ''}`}
          >
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            {isMobile ? "Back" : "Back to Resident Details"}
          </button>
        )}
        {!activeResidentId ? (
          <div className="adls-no-resident">
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <p className="adls-no-resident__title">No Resident Selected</p>
            <p className="adls-no-resident__subtitle">Please select a resident from the dropdown in the navigation bar</p>
          </div>
        ) : (
          <ErrorMessage error={error} onRetry={loadADLSData} residentId={activeResidentId} />
        )}
      </main>
    );
  }

    /* ──────────────────────── Render: Main Content ──────────────────────── */
  
  /**
   * Main Content Render
   * Displays the complete ADLS dashboard with activity cards and charts.
   * Layout adapts based on mobile/desktop viewport.
   */
        return (
    <main style={{
      flex: 1,
      padding: isMobile ? "8px" : "8px 60px",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: isMobile ? 8 : 10,
      minWidth: 0,
      position: "relative",
      zIndex: 1,
      paddingTop: isMobile ? (showBackButton ? 130 : 75) : (showBackButton ? 140 : 82),
      paddingBottom: 16,
      minHeight: "100vh",
      boxSizing: "border-box",
    }}>
      
      {/* Back to Resident Details Button */}
      {showBackButton && onBackToResident && (
        <button
          onClick={onBackToResident}
          className={`adls-back-button ${isMobile ? 'mobile' : ''}`}
        >
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          {isMobile ? "Back" : "Back to Resident Details"}
        </button>
      )}

                        {/* ──────────────────────── Activity Cards Section ──────────────────────── */}
            {/* 
              Iterate through each activity type (sit, walk, stand) and render:
              1. Left card: Activity info, resident name, and total duration
              2. Right card: Time-series bar chart showing activity throughout the day
            */}
                              {['sit', 'walk', 'stand'].map((activity) => {
        // Extract activity-specific data and metadata
        const activityData = adlsData[activity];
        // Skip rendering if activity data is missing
        if (!activityData || !activityData.totalDuration || !activityData.chartData) return null;
        const icon = activity === 'sit' ? sitIcon : activity === 'walk' ? walkIcon : standIcon;
        const activityName = activity.charAt(0).toUpperCase() + activity.slice(1);
        // Get proper gerund form for activity label
        const activityGerund = activity === 'sit' ? 'Sitting' : activity === 'walk' ? 'Walking' : 'Standing';
        
        return (
          <div key={activity} className={`adls-activity-grid ${isMobile ? 'mobile' : ''}`}>
            {/* ── Left Card: Activity Information ── */}
            <GCard variant="info" isMobile={isMobile}>
              <div className="adls-activity-info">
                {/* Activity details section */}
                <div className={`adls-activity-info__details ${isMobile ? 'mobile' : ''}`}>
                  <p className={`adls-activity-info__title ${isMobile ? 'mobile' : ''}`}>
                    Activity : {activityName}
                  </p>
                  
                  {/* Duration summary box */}
                  <div className={`adls-duration-box ${isMobile ? 'mobile' : ''}`}>
                                        <p className="adls-duration-box__label">
                      Yesterday's Total {activityGerund} Duration
                    </p>
                    <div className="adls-duration-box__time">
                      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span className={`adls-duration-box__value ${isMobile ? 'mobile' : ''}`}>
                        {activityData.totalDuration.hours}Hrs
                      </span>
                      <span className={`adls-duration-box__value ${isMobile ? 'mobile' : ''}`}>
                        {activityData.totalDuration.minutes}Mins
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Activity icon with circular border */}
                <div className={`adls-activity-icon ${isMobile ? 'mobile' : ''}`}>
                  <img src={icon} alt={activityName} />
                </div>
              </div>
            </GCard>

                                    {/* ── Right Card: Activity Chart ── */}
            <GCard variant="chart" isMobile={isMobile}>
              <div className="adls-chart-header">
                <div className="adls-chart-header__label-group">
                  <span className={`adls-chart-header__label ${isMobile ? 'mobile' : ''}`}>Mins</span>
                </div>
              </div>
              
              {/* Recharts bar chart showing activity duration over time */}
              <ResponsiveContainer width="100%" height={isMobile ? 140 : 160}>
                <BarChart data={activityData.chartData} margin={{ top: 8, right: 8, left: -10, bottom: 16 }} barGap={6}>
                  {/* Grid lines for chart readability */}
                  <CartesianGrid stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" vertical={false} />
                  {/* X-axis: Time labels */}
                  <XAxis 
                    dataKey="time" 
                    tick={{ fill: W, fontSize: isMobile ? 9 : 11, fontStyle: "italic" }} 
                    axisLine={{ stroke: "#ffffff", strokeWidth: 1.5 }} 
                    tickLine={false}
                    label={{ value: "Time", position: "insideBottom", offset: -10, fill: W, fontSize: 11, fontStyle: "italic" }}
                  />
                  {/* Y-axis: Duration in minutes (0-60) */}
                  <YAxis 
                    domain={[0, 60]} 
                    ticks={[0, 10, 20, 30, 40, 50, 60]}
                    tick={{ fill: W, fontSize: isMobile ? 9 : 11 }} 
                    axisLine={{ stroke: "#ffffff", strokeWidth: 1.5 }} 
                    tickLine={false}
                  />
                  {/* Bar component with custom shape */}
                  <Bar 
                    dataKey="duration" 
                    fill="#ffffff" 
                    shape={<CustomBar/>}
                    barSize={isMobile ? 8 : 12}
                  />
                </BarChart>
              </ResponsiveContainer>
            </GCard>
          </div>
        );
      })}
    </main>
  );
}
